import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMagic,
  faUtensils,
  faHeart,
  faMusic,
  faCompass,
  faTint,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type { ILandingPageRenderData } from '@share/common-types';
import styles from './experiences-grid-section.module.scss';

const ICON_MAP: Record<string, IconDefinition> = {
  dining: faUtensils,
  spa: faHeart,
  entertainment: faMusic,
  adventure: faCompass,
  pools: faTint,
  family: faUsers,
  default: faMagic,
};

function ExperienceIcon({ iconKey, className }: { iconKey: string; className?: string }): JSX.Element {
  const icon = ICON_MAP[iconKey.toLowerCase()] ?? ICON_MAP.default;
  return <FontAwesomeIcon icon={icon} className={className ?? styles.iconBlue} />;
}

type ExperiencesSurface = 'bg-white' | 'bg-light';

interface ExperiencesGridSectionProps {
  data: Pick<ILandingPageRenderData, 'experienceItems'>;
  /** Bootstrap 5 background utility — 'bg-white' or 'bg-light'. */
  sectionClassName?: ExperiencesSurface;
}

function resolveSectionClassName(sectionClassName: ExperiencesSurface): string {
  const surface =
    sectionClassName === 'bg-light' ? styles.sectionOnGray : `${styles.sectionOnWhite} bg-white`;

  return [styles.section, surface].filter(Boolean).join(' ');
}

/**
 * "Included experiences" — responsive 2–3 column cards with image, floating icon, title, description.
 */
export function ExperiencesGridSection({
  data,
  sectionClassName = 'bg-white',
}: ExperiencesGridSectionProps): JSX.Element | null {
  const items = data.experienceItems;
  if (!items || items.length === 0) return null;

  return (
    <section className={resolveSectionClassName(sectionClassName)}>
      <div className="container">
        <div className={styles.header}>
          <h2 className={styles.title}>More Than Just a Vacation</h2>
          <p className={styles.subtitle}>
            Every moment is curated for your enjoyment — from gourmet dining to thrilling adventures.
          </p>
        </div>

        <div className="row g-4">
          {items.map((item, index) => (
            <div key={index} className="col-12 col-sm-6 col-lg-4">
              <div className={styles.card}>
                <div className={styles.cardMedia}>
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt="" className={styles.cardMediaImg} />
                  ) : (
                    <div className={styles.cardMediaPlaceholder}>Add image</div>
                  )}
                  <div className={styles.cardMediaOverlay} />
                  <div className={styles.iconBadge}>
                    <ExperienceIcon iconKey={item.iconKey} className={styles.iconBlue} />
                  </div>
                </div>
                <div className={styles.cardBody}>
                  <h3 className={styles.cardTitle}>{item.title}</h3>
                  <p className={styles.cardDescription}>{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export { ExperienceIcon, ICON_MAP };
